import React, { Dispatch, FC, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { DrawerBody } from '@fiverr-private/overlay';
import { Divider, Stack, Wrap } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { I18n, translate as t } from '@fiverr-private/i18n-react';
import { Input } from '@fiverr-private/forms';
import { Button } from '@fiverr-private/button';
import { atoms } from '@fiverr-private/theme';
import { WorkExperience } from '../../../../gql-operations/gql-generated';
import { usePrePopulateLinkedinUrl } from '../../../../hooks/useImportWorkExperience';
import { CurrentViewState, WorkExperienceViewStates } from '../../shared/types';
import { T_KEY } from '../../utils';
import { uppercase } from '../../shared/styles.ve.css';
import { useMonitoring } from '../../../../utils/monitoring';
import workExperienceBIEvents from '../../utils/biEvents';
import { ImportImage } from './assets/ImportImage';
import ImportError from './ImportError';
import ImportLoading from './ImportLoading';
import useImportingLogic from './useImportingLogic';
import AddWorkExperienceButton from './AddWorkExperienceButton';

export enum ImportState {
    Initial,
    Loading,
    Error,
    Done,
}

interface ImportWorkExperienceProps {
    setCurrentViewState: Dispatch<SetStateAction<CurrentViewState>>;
    setCurrentWorkExperiences: (workExperiences: WorkExperience[] | undefined | null) => void;
    pushkinClient?;
}

const checkIfStartsWithLinkedinRegex = /^(https?:\/\/)?(?:www\.)?linkedin\.com.*$/;

const ImportWorkExperience: FC<ImportWorkExperienceProps> = ({
    setCurrentViewState,
    setCurrentWorkExperiences,
    pushkinClient,
}) => {
    const { Providers, fireEvents } = useMonitoring();
    const { data: linkedInData } = usePrePopulateLinkedinUrl({
        onSuccess: (data) => {
            if (data && !linkedInData) {
                setValue('profileUrl', data.profileUrl);
            }
        },
    });

    const { register, handleSubmit, formState, setValue, setError, watch } = useForm({
        defaultValues: {
            profileUrl: linkedInData?.profileUrl,
        },
        mode: 'all',
    });

    const profileUrlValue = watch('profileUrl');

    const { isTriggerLinkedInLoading, importState, triggerLinkedInImport } = useImportingLogic({
        pushkinClient,
        setCurrentWorkExperiences,
        setCurrentViewState,
        setError,
    });

    const onImportClick = (data: { profileUrl?: string }) => {
        if (data.profileUrl) {
            let profileUrl = data.profileUrl;
            if (!/^https?:\/\//.test(profileUrl)) {
                profileUrl = `https://${profileUrl}`;
            }

            triggerLinkedInImport(profileUrl);
            fireEvents({
                [Providers.BigQuery]: workExperienceBIEvents.clickOnImportWorkExperience(),
            });
        }
    };

    const onAddWorkExperienceClick = () => {
        setCurrentViewState((currentViewState) => ({
            ...currentViewState,
            viewState: WorkExperienceViewStates.AddEdit,
        }));
        fireEvents({
            [Providers.BigQuery]: workExperienceBIEvents.clickedOnAddWorkExperience(),
        });
    };

    const shouldShowLoading = isTriggerLinkedInLoading || importState === ImportState.Loading;
    const shouldShowError = importState === ImportState.Error;

    if (shouldShowError) {
        return (
            <DrawerBody>
                <ImportError
                    setCurrentViewState={setCurrentViewState}
                    profileUrl={profileUrlValue as string}
                    triggerLinkedInImport={triggerLinkedInImport}
                />
            </DrawerBody>
        );
    }

    if (shouldShowLoading) {
        return (
            <DrawerBody>
                <ImportLoading />
            </DrawerBody>
        );
    }

    return (
        <DrawerBody>
            <Stack direction="column" gap="8" alignItems="center" data-testId="import-work-experience">
                <ImportImage />
                <Text>
                    <I18n k={`${T_KEY}.import.description`} />
                </Text>
                <Stack direction="column" gap="10" width="100%">
                    <Wrap direction="column" gap="0">
                        <form>
                            <Wrap gap="4" justifyContent="flexEnd">
                                <Input
                                    placeholder={t(`${T_KEY}.import.placeholder`)}
                                    data-testId="linkedin-url-input"
                                    {...register('profileUrl', {
                                        pattern: {
                                            value: checkIfStartsWithLinkedinRegex,
                                            message: t(`${T_KEY}.import.input_error`),
                                        },
                                    })}
                                    isError={!!formState?.errors?.profileUrl}
                                    width="100%"
                                />
                                <Button
                                    onClick={handleSubmit(onImportClick)}
                                    data-testId="import-button"
                                    disabled={!!formState.errors?.profileUrl || !profileUrlValue}
                                    className={atoms({
                                        paddingX: '8',
                                    })}
                                >
                                    <I18n k={`${T_KEY}.import.cta`} />
                                </Button>
                            </Wrap>
                        </form>
                        <Text color="validationError" marginTop="2" hidden={!formState.errors.profileUrl?.message}>
                            {formState?.errors?.profileUrl?.message}
                        </Text>
                    </Wrap>
                    <Wrap gap="3" alignItems="center" wrap="nowrap">
                        <Divider width="50%" />
                        <Text className={uppercase} color="bodySecondary" fontWeight="semibold">
                            <I18n k={`${T_KEY}.import.or`} />
                        </Text>
                        <Divider width="50%" />
                    </Wrap>
                    <AddWorkExperienceButton onClick={onAddWorkExperienceClick} />
                </Stack>
            </Stack>
        </DrawerBody>
    );
};

export default ImportWorkExperience;
